import React, { useEffect, useState, memo } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  CardActionArea,
  Box,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Grid,
  Button
} from '@mui/material';
import { IconPhotoOff, IconEdit, IconRefresh, IconCashRegister, IconCircleCheck, IconCircleDashedCheck, IconCircleCheckFilled, IconCircleDotted, IconShare } from '@tabler/icons-react';
import Favorite from './Favorite';
import { API_CONFIG } from '../config';
import { useNavigate, Link } from "react-router-dom";
import { formatPrice, formatAttributeName, formatAttributeValue } from '../utils/formatting';
import { refreshListing, updateListingStatus } from '../services/ListingService';
import { notifyGlobal } from '../services/ServiceUtils';

/**
 * ListingCard component displays a single listing in a card format
 * @param {object} props - Component props
 * @param {object} props.listing - Listing data
 * @param {boolean} props.isOwner - Whether the current user owns this listing
 * @param {((payload: { listingId: number; isFav: boolean }) => void)} [props.onFavoriteChange] - Opcjonalny callback po zmianie ulubionych
 */
function ListingCard({ listing, isOwner = false, cardGridSize, onFavoriteChange }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [listingData, setListing] = useState(listing);
    
    // Sync listingData with prop changes (when parent updates listing)
    useEffect(() => {
      if (listing && listing.id === listingData?.id) {
        setListing(listing);
      }
    }, [listing, listingData?.id]);
  // Handle edit button click
  const handleEditClick = (e) => {
    e.stopPropagation();
    navigate(`/user/listing/edit/${listingData.id}`);
  };
// Handle active/deactive button click
  const handleActiveClick = async (e) => {
    e.stopPropagation();
    const statusId = e.target.checked ? 1 : 2;
    try {
      const { data, meta } = await updateListingStatus(listingData.id, statusId);
      if (meta) {
        console.warn('Listing status warning:', meta);
      }
      if (data) {
        setListing(data);
      } else {
        setListing(prev => ({ ...prev, status: statusId }));
      }
    } catch (err) {
      console.error('Error updating listing status:', err);
    }
  };
  // Handle refresh button click
  const handleRefreshClick = async (e) => {
    e.stopPropagation();
    try {
      const { data, meta } = await refreshListing(listingData.id);
      if (meta) {
        console.warn('Listing refresh warning:', meta);
        return;
      }
      if (data) {
        setListing((prev) => ({
          ...prev,
          dateTo: data,
        }));
      }
    } catch (err) {
      console.error('Error refreshing listing:', err);
    }
  };
  const handleShareClick = async (e) => {
    e.stopPropagation();
    const listingUrl = `${window.location.origin}/listing/${listingData.id}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(listingUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = listingUrl;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      notifyGlobal({
        level: 'success',
        message: 'Skopiowano link do ogłoszenia, możesz teraz podzielić się ogłoszeniem'
      });
    } catch (err) {
      console.error('Error copying listing link:', err);
    }
  };
  // Format listing price
  const price = listingData?.item?.latestPrice?.price
    ? formatPrice(listingData.item.latestPrice.price)
    : "Brak ceny";

  // Get image URL or use placeholder
  const hasImage = !!listingData?.item?.thumbnailUri;
  const imageUrl = hasImage 
    ? (listingData.item.thumbnailUri.startsWith('/') 
        ? `${API_CONFIG.BASE_URL}${listingData.item.thumbnailUri}` 
        : `${API_CONFIG.BASE_URL}/${listingData.item.thumbnailUri}`)
    : '';
  
  const renderAttributeList = (itemAttribute) => {
     if (itemAttribute) {
      return (
          formatAttributeName(itemAttribute.name, itemAttribute.jednostka).concat(": ", itemAttribute.itemAttributeValueDTO.map((itemAttributeValue) =>
            {
              return(
                " " + formatAttributeValue(itemAttributeValue) + (itemAttributeValue.itemAttributeDTO ? "| " + renderAttributeList(itemAttributeValue.itemAttributeDTO) : "")
              )
            }
            )) 
      );
  } else return "";
}
  return (
    <Grid
      item
      size={cardGridSize ? cardGridSize : { xs: 12, sm: 6, md: 4, lg: 4 }}
      sx={{ 
        display: 'flex',
        alignSelf: 'flex-start',
        height: 'auto',
        minHeight: 'auto'
      }}
    >
      <Card
        sx={{
          bgcolor: "info.light", // This will be overridden by MuiCard styleOverrides if card.bgcolor is set
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: 'auto',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          }
        }}
        elevation={2}
      >
        <CardActionArea
          component={Link}
          to={`/listing/${listingData.id}`}
          aria-label={`View listing: ${listingData.title}`}
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'stretch', 
            flexGrow: 0,
            flexShrink: 0,
            alignSelf: 'stretch',
            textDecoration: 'none',
            color: 'inherit'
          }}
        >
          {hasImage ? (
            <CardMedia
              sx={{
                objectFit: "cover",
                height: { xs: "200px", sm: "240px", md: "280px" },
                width: '100%',
                flexShrink: 0
              }}
              component="img"
              alt={`Image for ${listingData.title}`}
              src={imageUrl}
              loading="lazy"
            />
          ) : (
            <CardMedia
              sx={{
                objectFit: "cover",
                height: { xs: "200px", sm: "240px", md: "280px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => {
                  // Use card.placeholder.bgcolor from theme palette
                  return theme.palette.card?.placeholder?.bgcolor || theme.palette.grey[100];
                },
                flexShrink: 0
              }}
              aria-label="No image available"
            >
              <IconPhotoOff width="96" height="96" aria-hidden="true" />
            </CardMedia>
          )}
          <CardContent sx={{ 
            flexGrow: 0, 
            flexShrink: 0, 
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            '&:last-child': {
              paddingBottom: { xs: 1.5, sm: 2 }
            }
          }}>
            <Grid container spacing={1}>
              <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Typography
                  gutterBottom
                  variant={isMobile ? "subtitle2" : "subtitle1"}
                  sx={{
                    fontWeight: 520,
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minHeight: { xs: '1.5em', sm: '2.0em' },
                    lineHeight: 1.2,
                    margin: 0,
                    padding: 0.25
                  }}
                >
                  {listingData.title}
                </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                   {/* Display card attributes */}
                  <Typography 
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    ml: 0.5,
                  
                  }}>
                  {listingData?.item?.itemAttributes?.map((itemAttribute, index) => {
                    return  renderAttributeList(itemAttribute) + (index < listingData.item.itemAttributes.length-1  ? " | " : "")
                  })}
                  </Typography>  
                </Grid>
                <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                  {/* Display address */}
                {listingData?.address && (
                  <Typography
                    key={listingData.address.id}
                    variant="body2"
                    component="p"
                    sx={{
                      right: 0,
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                      ml: 0.5,
                    
                    }}
                  >
                    {listingData.address.city? listingData.address.city + ', ': ''}  {listingData.address.voivodeship? listingData.address.voivodeship + ', ': '' } {listingData.address.country? listingData.address.country : ''}
                  </Typography>
                )}
                </Grid>
                 {isOwner && (
                  <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                  <Typography
                  variant="body2"
                  component="p"
                  sx={{
                    right: 0,
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    ml: 0.5,
                   
                  }}
                >
                Aktywne od: {listingData.dateFrom ? new Date(listingData.dateFrom).toLocaleDateString('pl-PL') : ''}{listingData.dateTo ? ' do ' + new Date(listingData.dateTo).toLocaleDateString('pl-PL') : ''} 
                </Typography>
                </Grid>
                 )} 
                {isOwner && (
                <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                 <Typography
                  variant="body2"
                  component="p"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    ml: 0.5,
                   
                  }}
                >
                  Wyświetlenia: {listingData.viewsCount}
                </Typography>
                </Grid>
                 )} 

            </Grid>

          </CardContent>
        </CardActionArea>
        <CardActions
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            px: { xs: 1.5, sm: 2 },
            py: 1,
            borderTop: 1,
            borderColor: (theme) => theme.palette.card?.actions?.borderColor || theme.palette.divider
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              variant={isMobile ? "subtitle2" : "h6"}
              component="p"
              sx={{ fontWeight: "bold", color: "primary.main" }}
            >
              {price}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Udostępnij ogłoszenie">
                <IconButton aria-label="udostępnij ogłoszenie" onClick={handleShareClick}>
                  <IconShare size={20} />
                </IconButton>
              </Tooltip>
              <Favorite
                listingId={listingData.id}
                isFav={!!listingData?.favorite?.id}
                onFavoriteChange={onFavoriteChange}
              />
            </Box>
          </Box>

          {isOwner && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                mt: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                startIcon={
                  (() => {
                    switch(listingData.status) {
                    case 1: return <IconCircleCheckFilled size={20} />;
                    case 2: return <IconCircleCheck size={20} />;
                    case 3: return <IconCircleDashedCheck size={20} />;
                    case 4: return <IconCashRegister size={20} />;
                    default: return <IconCircleDotted size={20} />;
                    }
                    })()
                }
                onChange={handleActiveClick}
              >
                {(() => {
                  switch(listingData.status) {
                  case 1: return "Dezaktywuj";
                  case 2: return "Aktywuj";
                  case 3: return "Weryfikacja";
                  case 4: return "Opłać";
                  default: return "Nieznany status";
                  }
                  })()
                }
              </Button>

              <Button
                aria-label="edit listing"
                onClick={handleEditClick}
                sx={{ color: "primary.main" }}
                startIcon={<IconEdit size={20} />}
              >
                Edytuj
              </Button>

              <Button
                aria-label="refresh listing"
                onClick={handleRefreshClick}
                sx={{ color: "primary.main" }}
                startIcon={<IconRefresh size={20} />}
              >
                Odśwież
              </Button>
            </Box>
          )}
        </CardActions>
      </Card>
    </Grid>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(ListingCard);